import React, { useState, useEffect, useRef } from "react";
import "./Hero.css";
import "animate.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function Hero({
  selectedTrack,
  onNewThread,
  isLoggedIn,
  username,
  setUsernamePopup,
}) {
  const [content, setContent] = useState("");
  const [writeThread, setWriteThread] = useState(false);
  const msgContentRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        msgContentRef.current &&
        !msgContentRef.current.contains(event.target)
      ) {
        setWriteThread(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    if (!token) {
      toast.error("You must be logged in to post a thread.");
      return;
    }

    if (!selectedTrack?.id) {
      toast.error("No track selected.");
      return;
    }

    const newThread = {
      username: username,
      content,
      timestamp: new Date().toISOString(),
      songID: selectedTrack.id,
    };

    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/thread/post`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newThread),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.error || errorData.err || "Failed to create thread"
        );
      }

      const data = await res.json();
      onNewThread(data.thread);
      setWriteThread(false);
      setContent("");
      toast.success("Thread posted successfully!");
    } catch (error) {
      toast.error(`Error saving data: ${error.message}`);
      console.error("Thread submission error:", error);
    }
  };

  return (
    <>
      {writeThread && (
        <div className="form-overlay">
          <div className="form-container" ref={msgContentRef}>
            <h3 className="form-heading">Start a New Thread</h3>
            <form onSubmit={handleSubmit}>
              <textarea
                rows="8"
                name="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Share your thoughts about this song..."
                required
              ></textarea>
              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setWriteThread(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedTrack && (
        <div className="hero-container">
          <div className="hero-main">
            {/* Interactive 3D artwork cover with slide-out vinyl */}
            <div className="cover-art-container">
              <div className="vinyl-plate-behind">
                <div
                  className="vinyl-plate-label"
                  style={{
                    backgroundImage: `url(${selectedTrack.image || "default-image.jpg"})`,
                  }}
                ></div>
              </div>
              <div
                className="cover"
                style={{
                  backgroundImage: `url(${selectedTrack.image || "default-image.jpg"})`,
                }}
              ></div>
            </div>

            <div className="info">
              <div className="popularity">
                <span className="pill popularity-pill">
                  <svg className="pill-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  Popularity: {selectedTrack.popularity || "0"}%
                </span>
                
                <span className="pill duration-pill">
                  <svg className="pill-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  {selectedTrack.duration_ms
                    ? `${Math.floor(selectedTrack.duration_ms / 60000)}:${Math.floor(
                        (selectedTrack.duration_ms % 60000) / 1000
                      )
                        .toString()
                        .padStart(2, "0")}`
                    : "Unavailable"}
                </span>
              </div>
              
              <div className="title">
                <span>{selectedTrack.name || "Unknown Song"}</span>
                <div className="track-equalizer-gif">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              
              <div className="artist">
                <span>{selectedTrack.artist || "Unknown Artist"}</span>
              </div>
            </div>

            <div className="cta-container">
              <button
                className="cta-btn primary-cta"
                onClick={() =>
                  isLoggedIn ? setWriteThread(true) : setUsernamePopup(true)
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="cta-icon"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z"
                  />
                </svg>
                <span>Start Thread</span>
              </button>

              <button
                className="cta-btn spotify-cta"
                onClick={() => window.open(selectedTrack.url, "_blank")}
                title="Play on Spotify"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  className="cta-icon spotify-icon"
                >
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.496 17.306c-.215.352-.676.463-1.028.248-2.856-1.745-6.452-2.14-10.686-1.173-.404.092-.81-.157-.902-.56-.092-.403.157-.81.56-.902 4.636-1.06 8.59-.607 11.78 1.34.353.216.464.677.248 1.028zm1.467-3.258c-.27.439-.848.585-1.288.315-3.268-2.008-8.25-2.592-12.11-1.42-.494.15-1.018-.129-1.168-.623-.15-.495.13-1.02.624-1.169 4.41-1.339 9.897-.687 13.629 1.61.439.27.585.849.313 1.287zm.126-3.415C15.095 8.16 8.528 7.94 4.745 9.09c-.615.186-1.264-.17-1.45-1.786-.187-.616.168-1.265.783-1.45 4.33-1.314 11.583-1.06 16.16 1.66.55.326.732 1.033.407 1.583-.326.55-1.033.731-1.583.406z" />
                </svg>
                <span>Spotify</span>
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer
        position={window.innerWidth < 768 ? "bottom-center" : "top-right"}
        autoClose={2000}
        hideProgressBar
        toastStyle={{ backgroundColor: "#000", color: "#fff" }}
      />
    </>
  );
}

export default Hero;
